// Pure mapping from GET /dashboard/summary to the Home priority KPIs. No React, no labels:
// components resolve the label keys.
//
// Permissions are decided by the server: a section the user cannot read is ABSENT from
// the summary and produces nothing here; a section that failed server-side is null and
// produces an "unavailable" KPI, so a failure is never shown as a reassuring 0.

// UX brief: the Home shows at most 6 priority, actionable KPIs.
export const MAX_KPIS = 6;

// Tank fill (%) from which the occupancy KPI asks for attention.
const NEARLY_FULL_PCT = 90;

const isPresent = (section) => section !== undefined;

const unavailable = (id, labelKey, to) => ({
  id,
  labelKey,
  value: null,
  status: "unavailable",
  to,
});

const statusFor = (count, nonZeroStatus) => (count > 0 ? nonZeroStatus : "ok");

const operationsKpis = (operations) => {
  if (!isPresent(operations)) return [];
  if (operations === null) {
    return [
      unavailable("operations_completed_today", "homeKpiOperationsCompletedToday", "/programming"),
      unavailable("operations_overdue", "homeKpiOperationsOverdue", "/programming"),
    ];
  }
  return [
    {
      id: "operations_completed_today",
      labelKey: "homeKpiOperationsCompletedToday",
      value: operations.completed_today ?? 0,
      status: "neutral",
      contextKey: "homeKpiOperationsCompletedTodayContext",
      // What is still to be done today, next to what is already done.
      contextTokens: { count: operations.today },
      to: "/programming",
    },
    {
      id: "operations_overdue",
      labelKey: "homeKpiOperationsOverdue",
      value: operations.overdue,
      status: statusFor(operations.overdue, "critical"),
      contextKey: "homeKpiOperationsOverdueContext",
      contextTokens: { count: operations.expiring_7d },
      to: "/programming",
    },
  ];
};

const alertsKpi = (alerts) => {
  if (!isPresent(alerts)) return [];
  if (alerts === null) return [unavailable("alerts_critical", "homeKpiAlertsCritical", "/alerts")];
  return [
    {
      id: "alerts_critical",
      labelKey: "homeKpiAlertsCritical",
      value: alerts.critical,
      status: statusFor(alerts.critical, "critical"),
      contextKey: "homeKpiAlertsCriticalContext",
      contextTokens: { count: alerts.active },
      to: "/alerts",
    },
  ];
};

const lotsKpi = (lots) => {
  if (!isPresent(lots)) return [];
  if (lots === null) return [unavailable("lots_active", "homeKpiLotsActive", "/lots")];
  return [
    {
      id: "lots_active",
      labelKey: "homeKpiLotsActive",
      value: lots.active,
      status: "neutral",
      contextKey: "homeKpiLotsActiveContext",
      contextTokens: { count: lots.by_state.length },
      to: "/lots",
    },
  ];
};

const warehouseKpi = (warehouse) => {
  if (!isPresent(warehouse)) return [];
  if (warehouse === null) return [unavailable("stock_below_min", "homeKpiStockBelowMin", "/warehouse")];
  return [
    {
      id: "stock_below_min",
      labelKey: "homeKpiStockBelowMin",
      value: warehouse.drugs_below_min,
      status: statusFor(warehouse.drugs_below_min, "warning"),
      contextKey: "homeKpiStockBelowMinContext",
      contextTokens: { count: warehouse.drug_batches_expired },
      to: "/warehouse",
    },
  ];
};

const tanksKpi = (tanks) => {
  if (!isPresent(tanks)) return [];
  if (tanks === null) return [unavailable("tanks_occupancy", "homeKpiTanksOccupancy", "/tanks")];
  // No tanks (or no capacity) configured: a percentage would be meaningless.
  if (!tanks.total || !tanks.capacity_l) return [];
  const pct = Math.round((tanks.quantity_l / tanks.capacity_l) * 100);
  return [
    {
      id: "tanks_occupancy",
      labelKey: "homeKpiTanksOccupancy",
      value: pct,
      unit: "%",
      status: pct >= NEARLY_FULL_PCT ? "warning" : "neutral",
      contextKey: "homeKpiTanksOccupancyContext",
      contextTokens: { occupied: tanks.occupied, total: tanks.total },
      to: "/tanks",
    },
  ];
};

export const buildKpis = (summary) => {
  if (!summary) return [];
  return [
    ...operationsKpis(summary.operations),
    ...alertsKpi(summary.alerts),
    ...warehouseKpi(summary.warehouse),
    ...lotsKpi(summary.lots),
    ...tanksKpi(summary.tanks),
  ].slice(0, MAX_KPIS);
};
